/**
 * ImportWebsitePanel — the compliance-gated "Import from my existing
 * website" tool inside BusinessEditPage. Handles the mandatory consent
 * flow (typed signature + checkbox), calls the backend to fetch +
 * LLM-structure the site, then lets the owner selectively apply the
 * draft onto the business profile.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import {
  Download, AlertTriangle, CheckCircle2, ExternalLink,
  Loader2, Save,
} from "lucide-react";

function PreviewCard({ title, body }) {
  if (!body) return null;
  return (
    <div className="wofy-card !p-4">
      <div className="overline mb-1.5">{title}</div>
      <p className="text-sm whitespace-pre-line leading-relaxed">{body}</p>
    </div>
  );
}

export default function ImportWebsitePanel({ biz, onChange }) {
  const [disc, setDisc] = useState(null);
  const [url, setUrl] = useState(biz.website || "");
  const [signature, setSignature] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [importId, setImportId] = useState(null);
  const [selected, setSelected] = useState({ description: true, mission: true, vision: true, hero: true, pages: {} });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/website-import/disclaimer");
        setDisc(data);
      } catch (_) { /* noop */ }
    })();
  }, []);

  const runImport = async () => {
    if (!agreed) return toast.error("Please confirm the Content Import Authorization.");
    if (!signature.trim()) return toast.error("Please type your full name to sign.");
    setBusy(true);
    try {
      const { data } = await http.post(`/businesses/${biz.id}/import-website`, {
        url: url.trim(),
        consent_agreed: true,
        consent_signature: signature.trim(),
      });
      setDraft(data.draft);
      setImportId(data.import_id);
      const sel = {};
      (data.draft.custom_pages || []).forEach((p) => { sel[p.id] = true; });
      setSelected((s) => ({ ...s, pages: sel }));
      toast.success("Draft ready — review and apply below.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed. Make sure the site is public and reachable.");
    } finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const cp_ids = Object.entries(selected.pages).filter(([, v]) => v).map(([k]) => k);
      const { data } = await http.post(`/businesses/${biz.id}/apply-import`, {
        import_id: importId,
        apply_description: selected.description,
        apply_mission:     selected.mission,
        apply_vision:      selected.vision,
        apply_hero:        selected.hero,
        apply_custom_pages: cp_ids.length > 0,
        custom_page_ids:   cp_ids,
      });
      toast.success(`Imported: ${data.applied.join(", ") || "no changes"}.`);
      setDraft(null);
      setImportId(null);
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="import-website-panel">
      {!draft && (
        <div className="wofy-card !p-6 space-y-4">
          <div className="flex items-start gap-3">
            <Download className="w-6 h-6" style={{ color: "var(--wofy-primary)" }} />
            <div className="min-w-0">
              <h2 className="font-display text-2xl">Import from your existing website</h2>
              <p className="text-sm opacity-80 mt-1">
                Paste the URL of your existing site — we'll fetch it, extract your About / Services / hero
                image / mission and draft matching pages on your Social Pulse profile. You review everything
                before it goes live.
              </p>
            </div>
          </div>

          <label className="block">
            <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">Website URL *</div>
            <input value={url} onChange={(e) => setUrl(e.target.value)} className="wofy-input" placeholder="https://your-business.com" data-testid="iw-url" />
          </label>

          <div className="rounded-lg border p-4 space-y-2 bg-amber-50/60" style={{ borderColor: "#F59E0B" }} data-testid="iw-disclaimer">
            <div className="inline-flex items-center gap-1.5 font-semibold text-sm text-amber-900">
              <AlertTriangle className="w-4 h-4" /> Content Import Authorization
            </div>
            <p className="text-xs whitespace-pre-line text-amber-950/90 leading-relaxed">
              {disc?.text || "Loading authorization text…"}
            </p>
            <label className="flex items-start gap-2 text-xs pt-1">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="iw-consent-checkbox" className="mt-0.5" />
              <span>
                I confirm that <b>I own this website or have the legal authority and permission</b> to import and use its content on SocialPulse.
              </span>
            </label>
            <label className="block">
              <div className="text-[10px] font-mono uppercase tracking-wider opacity-70 mt-1">Type your full name to sign</div>
              <input
                value={signature} onChange={(e) => setSignature(e.target.value)}
                className="wofy-input !py-1.5 text-sm mt-0.5" data-testid="iw-signature"
                placeholder="e.g. Priya Patel"
              />
            </label>
            {disc?.version && <div className="text-[10px] opacity-60 font-mono mt-1">Version: {disc.version}</div>}
          </div>

          <div className="text-right">
            <button
              onClick={runImport}
              disabled={busy || !url.trim() || !agreed || !signature.trim()}
              className="wofy-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="iw-run"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Import Website
            </button>
          </div>
        </div>
      )}

      {draft && (
        <div className="space-y-3" data-testid="iw-preview">
          <div className="wofy-card !p-4">
            <div className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 mb-2">
              <CheckCircle2 className="w-4 h-4" /> Draft ready — pick what to apply
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              <label className="text-sm inline-flex items-center gap-2">
                <input type="checkbox" checked={selected.description} onChange={(e) => setSelected({ ...selected, description: e.target.checked })} data-testid="iw-pick-description" /> About / description
              </label>
              <label className="text-sm inline-flex items-center gap-2">
                <input type="checkbox" checked={selected.mission} onChange={(e) => setSelected({ ...selected, mission: e.target.checked })} data-testid="iw-pick-mission" /> Mission
              </label>
              <label className="text-sm inline-flex items-center gap-2">
                <input type="checkbox" checked={selected.vision} onChange={(e) => setSelected({ ...selected, vision: e.target.checked })} data-testid="iw-pick-vision" /> Vision
              </label>
              <label className="text-sm inline-flex items-center gap-2">
                <input type="checkbox" checked={selected.hero} onChange={(e) => setSelected({ ...selected, hero: e.target.checked })} data-testid="iw-pick-hero" /> Hero image
              </label>
            </div>
          </div>
          <PreviewCard title="About us" body={draft.about_us} />
          <div className="grid md:grid-cols-2 gap-3">
            <PreviewCard title="Mission" body={draft.mission} />
            <PreviewCard title="Vision"  body={draft.vision} />
          </div>
          {draft.hero_image_url && (
            <div className="wofy-card !p-4">
              <div className="overline mb-2">Hero image</div>
              <img src={draft.hero_image_url} alt="" className="rounded-lg max-h-56 mx-auto" />
              <a href={draft.hero_image_url} target="_blank" rel="noreferrer" className="text-[10px] opacity-60 inline-flex items-center gap-1 mt-1"><ExternalLink className="w-3 h-3" /> Source</a>
            </div>
          )}
          {(draft.custom_pages || []).length > 0 && (
            <div className="wofy-card !p-4">
              <div className="overline mb-2">Custom pages to create ({draft.custom_pages.length})</div>
              <ul className="space-y-2">
                {draft.custom_pages.map((p) => (
                  <li key={p.id} className="border rounded-lg p-3" style={{ borderColor: "var(--wofy-border)" }} data-testid={`iw-page-${p.slug}`}>
                    <label className="flex items-start gap-2">
                      <input type="checkbox" checked={!!selected.pages[p.id]} onChange={(e) => setSelected({ ...selected, pages: { ...selected.pages, [p.id]: e.target.checked } })} className="mt-1" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">{p.title}</div>
                        <div className="text-[10px] font-mono opacity-70">/{p.slug}</div>
                        <div className="text-xs opacity-80 mt-1 line-clamp-3" dangerouslySetInnerHTML={{ __html: p.body_html }} />
                      </div>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex items-center justify-between pt-3">
            <button onClick={() => { setDraft(null); setImportId(null); }} className="wofy-btn-ghost" data-testid="iw-discard">Discard draft</button>
            <button onClick={apply} disabled={busy} className="wofy-btn-primary inline-flex items-center gap-2" data-testid="iw-apply">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Apply selected
            </button>
          </div>
          <p className="text-[10px] opacity-60 text-center pt-2">
            Your Content Import Authorization has been saved to your profile — see it under <b>Profile → My imports</b>.
          </p>
        </div>
      )}
    </div>
  );
}
